import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  helperText?: string;
  required?: boolean;
  size?: 'small' | 'medium';
};

function parseDateOnlyValue(
  value: string,
) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(
    value,
  );

  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const parsedDate = new Date(
    year,
    month - 1,
    day,
  );

  if (
    parsedDate.getFullYear() !== year
    || parsedDate.getMonth() !== month - 1
    || parsedDate.getDate() !== day
  ) {
    return null;
  }

  return parsedDate;
}

function parseOperationalDateValue(
  value: string | null | undefined,
) {
  if (!value) {
    return null;
  }

  const parsedDateOnly = parseDateOnlyValue(
    value,
  );

  if (parsedDateOnly) {
    return parsedDateOnly;
  }

  const parsedDateTime = new Date(value);

  if (
    Number.isNaN(
      parsedDateTime.getTime(),
    )
  ) {
    return null;
  }

  return new Date(
    parsedDateTime.getFullYear(),
    parsedDateTime.getMonth(),
    parsedDateTime.getDate(),
  );
}

function getLocalCalendarDayNumber(
  value: Date,
) {
  return Date.UTC(
    value.getFullYear(),
    value.getMonth(),
    value.getDate(),
  ) / (24 * 60 * 60 * 1000);
}

export function getOperationalDayDifference(
  value: string | null | undefined,
  referenceDate = new Date(),
) {
  const parsedDate =
    parseOperationalDateValue(value);

  if (!parsedDate) {
    return null;
  }

  return (
    getLocalCalendarDayNumber(parsedDate)
    - getLocalCalendarDayNumber(
        referenceDate,
      )
  );
}

export function formatOperationalRelativeDay(
  value: string | null | undefined,
) {
  const differenceInDays =
    getOperationalDayDifference(value);

  if (differenceInDays === null) {
    return null;
  }

  if (differenceInDays < 0) {
    const overdueDays =
      Math.abs(differenceInDays);

    return overdueDays === 1
      ? 'hace 1 día'
      : `hace ${overdueDays} días`;
  }

  if (differenceInDays === 0) {
    return 'hoy';
  }

  if (differenceInDays === 1) {
    return 'mañana';
  }

  return `en ${differenceInDays} días`;
}

export function formatOperationalDate(
  value: string | null | undefined,
  emptyLabel = 'Sin fecha',
) {
  const parsedDate =
    parseOperationalDateValue(value);

  if (!parsedDate) {
    return emptyLabel;
  }

  return new Intl.DateTimeFormat(
    'es-ES',
    {
      dateStyle: 'short',
    },
  ).format(parsedDate);
}

export function toDateOnlyInputValue(
  value: string | null | undefined,
) {
  const parsedDate =
    parseOperationalDateValue(value);

  if (!parsedDate) {
    return '';
  }

  return format(parsedDate, 'yyyy-MM-dd');
}

export function toEndOfLocalDayIso(
  value: string,
) {
  const parsedDate = parseDateOnlyValue(value);

  if (!parsedDate) {
    return null;
  }

  parsedDate.setHours(
    23,
    59,
    59,
    999,
  );

  return parsedDate.toISOString();
}

export default function IndustrialWorkflowDatePicker({
  label,
  value,
  onChange,
  helperText,
  required = false,
  size = 'medium',
}: Props) {
  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={es}
    >
      <DatePicker
        label={label}
        value={
          value
            ? parseDateOnlyValue(value)
            : null
        }
        onChange={(selectedDate) => {
          if (
            !selectedDate
            || Number.isNaN(
              selectedDate.getTime(),
            )
          ) {
            onChange('');

            return;
          }

          onChange(
            format(
              selectedDate,
              'yyyy-MM-dd',
            ),
          );
        }}
        format="dd/MM/yyyy"
        slotProps={{
          textField: {
            size,
            fullWidth: true,
            required,
            helperText,
            sx: {
              '& .MuiInputBase-root': {
                color: '#f8fafc',
              },
              '& .MuiInputLabel-root': {
                color: '#94a3b8',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor:
                  'rgba(148,163,184,0.28)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor:
                  'rgba(56,189,248,0.46)',
              },
              '& .MuiSvgIcon-root': {
                color: '#7dd3fc',
              },
            },
          },
          actionBar: {
            actions: [
              'clear',
              'today',
            ],
          },
        }}
      />
    </LocalizationProvider>
  );
}